import { useEffect, useState } from 'react';
import type { fabric } from 'fabric';
import {
  alignSelection,
  distributeSelection,
  groupSelection,
  ungroupSelection,
  type AlignMode,
  type DistributeMode,
} from '@/lib/objectOps';
import { applyClippingMask, clearClipPath } from '@/lib/frames';
import { applyGradientFill } from '@/lib/gradients';
import { removeBackgroundFromImage } from '@/lib/removeBg';
import { applyCartoonToImage } from '@/lib/cartoonEffect';
import {
  applyImageFilters,
  readImageFilters,
  resetImageFilters,
  type ImageFilterValues,
} from '@/lib/imageFilters';
import { applyBeautyPreset, type BeautyPresetId } from '@/lib/beautyPresets';
import { OPEN_PHOTO_STUDIO_EVENT } from '@/lib/photoStudioEvents';
import { OPEN_ANIMATION_PANEL_EVENT } from '@/lib/video/videoEvents';
import { isVideoObject } from '@/lib/video/fabricVideoObject';
import VideoClipProperties from './video/VideoClipProperties';
import { BLEND_MODES, normalizeBlendMode, type BlendModeId } from '@/lib/blendModes';
import { flipSelection, rotateSelection } from '@/lib/transformOps';
import { applyBooleanSelection, type BooleanMode } from '@/lib/booleanOps';
import { canPlaceTextOnPath, placeTextOnPath } from '@/lib/textOnPath';
import { applyObjectShadow, readObjectShadow, type ShadowValues } from '@/lib/objectShadow';
import { FONT_FAMILIES } from '@/data/fontFamilies';
import { useCanvasStore } from '@/stores/canvasStore';

interface PropertiesPanelProps {
  canvas: fabric.Canvas | null;
  ready: boolean;
  onChange?: () => void;
  /** Animate and clip timing belong to Movie Studio. */
  motionTools?: boolean;
}

interface PropsState {
  type: string;
  left: number;
  top: number;
  width: number;
  height: number;
  angle: number;
  opacity: number;
  fill: string;
  stroke: string;
  strokeWidth: number;
  cornerRadius: number;
  blendMode: BlendModeId;
  text?: string;
  fontSize?: number;
  fontFamily?: string;
  fontWeight?: string | number;
  fontStyle?: string;
  underline?: boolean;
  textAlign?: string;
}

function isBoldWeight(weight: string | number | undefined): boolean {
  if (weight === 'bold') return true;
  const n = Number(weight);
  return Number.isFinite(n) && n >= 600;
}

function hexFill(value: unknown): string {
  if (typeof value === 'string' && /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(value)) {
    return value.length === 4
      ? `#${value[1]}${value[1]}${value[2]}${value[2]}${value[3]}${value[3]}`
      : value;
  }
  return '#000000';
}

export default function PropertiesPanel({
  canvas,
  ready,
  onChange,
  motionTools = false,
}: PropertiesPanelProps) {
  const [props, setProps] = useState<PropsState | null>(null);
  const {
    selectedColor,
    gradientToColor,
    setSelectedColor,
    setGradientToColor,
  } = useCanvasStore();
  const [bgBusy, setBgBusy] = useState(false);
  const [cartoonBusy, setCartoonBusy] = useState(false);
  const [bgNote, setBgNote] = useState<string | null>(null);
  const [booleanNote, setBooleanNote] = useState<string | null>(null);
  const [pathText, setPathText] = useState('Curved text');
  const [pathTextNote, setPathTextNote] = useState<string | null>(null);
  const [filters, setFilters] = useState<ImageFilterValues>({
    brightness: 0,
    contrast: 0,
    blur: 0,
    saturation: 0,
    grayscale: false,
  });
  const [shadow, setShadow] = useState<ShadowValues>({
    enabled: false,
    color: '#000000',
    blur: 8,
    offsetX: 4,
    offsetY: 4,
  });

  useEffect(() => {
    if (!canvas || !ready) return;

    const sync = () => {
      const obj = canvas.getActiveObject();
      if (!obj) {
        setProps(null);
        return;
      }

      const bound = obj.getBoundingRect(true);
      const textObj = obj as fabric.IText;
      const rect = obj as fabric.Rect;
      setProps({
        type: (obj.type || 'object').toString(),
        left: Math.round(obj.left || 0),
        top: Math.round(obj.top || 0),
        width: Math.round(bound.width),
        height: Math.round(bound.height),
        angle: Math.round(obj.angle || 0),
        opacity: Math.round((obj.opacity ?? 1) * 100) / 100,
        fill: hexFill(obj.fill),
        stroke: hexFill(obj.stroke || '#000000'),
        strokeWidth: Math.round((obj.strokeWidth || 0) * 10) / 10,
        cornerRadius: Math.round(rect.rx || 0),
        blendMode: normalizeBlendMode(obj.globalCompositeOperation),
        text: typeof textObj.text === 'string' ? textObj.text : undefined,
        fontSize:
          typeof textObj.fontSize === 'number' ? Math.round(textObj.fontSize) : undefined,
        fontFamily:
          typeof textObj.fontFamily === 'string' ? textObj.fontFamily : undefined,
        fontWeight: textObj.fontWeight,
        fontStyle: textObj.fontStyle,
        underline: Boolean(textObj.underline),
        textAlign: textObj.textAlign,
      });
      setShadow(readObjectShadow(obj));
      if (obj.type === 'image') {
        setFilters(readImageFilters(obj as fabric.Image));
      }
    };

    const events = [
      'selection:created',
      'selection:updated',
      'selection:cleared',
      'object:modified',
      'object:scaling',
      'object:moving',
      'object:rotating',
    ] as const;

    events.forEach((ev) => canvas.on(ev, sync));
    sync();
    return () => {
      events.forEach((ev) => canvas.off(ev, sync));
    };
  }, [canvas, ready]);

  const apply = (patch: Partial<PropsState>) => {
    if (!canvas) return;
    const obj = canvas.getActiveObject();
    if (!obj) return;

    if (patch.left !== undefined) obj.set('left', patch.left);
    if (patch.top !== undefined) obj.set('top', patch.top);
    if (patch.angle !== undefined) obj.set('angle', patch.angle);
    if (patch.opacity !== undefined) obj.set('opacity', patch.opacity);
    if (patch.fill !== undefined) obj.set('fill', patch.fill);
    if (patch.stroke !== undefined) obj.set('stroke', patch.stroke);
    if (patch.strokeWidth !== undefined) {
      obj.set('strokeWidth', Math.max(0, patch.strokeWidth));
    }
    if (patch.cornerRadius !== undefined && obj.type === 'rect') {
      const r = Math.max(0, patch.cornerRadius);
      (obj as fabric.Rect).set({ rx: r, ry: r });
    }
    if (patch.blendMode !== undefined) {
      obj.set('globalCompositeOperation', patch.blendMode);
    }

    const textObj = obj as fabric.IText;
    if (patch.text !== undefined && 'text' in textObj) textObj.set('text', patch.text);
    if (patch.fontSize !== undefined && 'fontSize' in textObj) {
      textObj.set('fontSize', patch.fontSize);
    }
    if (patch.fontFamily !== undefined && 'fontFamily' in textObj) {
      textObj.set('fontFamily', patch.fontFamily);
    }
    if (patch.fontWeight !== undefined && 'fontWeight' in textObj) {
      textObj.set('fontWeight', patch.fontWeight);
    }
    if (patch.fontStyle !== undefined && 'fontStyle' in textObj) {
      textObj.set(
        'fontStyle',
        (patch.fontStyle === 'italic' ? 'italic' : 'normal') as '' | 'normal' | 'italic' | 'oblique'
      );
    }
    if (patch.underline !== undefined && 'underline' in textObj) {
      textObj.set('underline', Boolean(patch.underline));
    }
    if (patch.textAlign !== undefined && 'textAlign' in textObj) {
      textObj.set('textAlign', patch.textAlign as 'left' | 'center' | 'right' | 'justify');
    }

    // Refresh text metrics after style changes
    if (
      patch.fontFamily !== undefined ||
      patch.fontWeight !== undefined ||
      patch.fontStyle !== undefined ||
      patch.underline !== undefined ||
      patch.fontSize !== undefined ||
      patch.text !== undefined
    ) {
      const withInit = textObj as fabric.IText & { initDimensions?: () => void };
      if (typeof withInit.initDimensions === 'function') withInit.initDimensions();
      textObj.dirty = true;
    }

    obj.setCoords();
    canvas.requestRenderAll();
    setProps((prev) => (prev ? { ...prev, ...patch } : prev));
    onChange?.();
  };

  const bring = (dir: 'front' | 'forward' | 'backward' | 'back') => {
    if (!canvas) return;
    const obj = canvas.getActiveObject();
    if (!obj) return;
    if (dir === 'front') canvas.bringToFront(obj);
    if (dir === 'forward') canvas.bringForward(obj);
    if (dir === 'backward') canvas.sendBackwards(obj);
    if (dir === 'back') canvas.sendToBack(obj);
    canvas.requestRenderAll();
    onChange?.();
  };

  const align = (mode: AlignMode) => {
    if (!canvas) return;
    if (alignSelection(canvas, mode)) onChange?.();
  };

  const distribute = (mode: DistributeMode) => {
    if (!canvas) return;
    if (distributeSelection(canvas, mode)) onChange?.();
  };

  const group = () => {
    if (!canvas) return;
    if (groupSelection(canvas)) onChange?.();
  };

  const ungroup = () => {
    if (!canvas) return;
    if (ungroupSelection(canvas)) onChange?.();
  };

  const pathfinder = (mode: BooleanMode) => {
    if (!canvas) return;
    const result = applyBooleanSelection(canvas, mode);
    setBooleanNote(result.note);
    if (result.ok) onChange?.();
  };

  const selectionCount = canvas?.getActiveObjects().length ?? 0;
  const activeObj = canvas?.getActiveObject() || null;
  const activeType = activeObj?.type;
  const pathTextReady = canPlaceTextOnPath(activeObj);

  if (!props) {
    return (
      <div className="ps-props">
        <h3>Properties</h3>
        <p className="ps-props-empty">
          Select a layer or object on the artboard to edit position, fill, opacity, and type
          settings.
        </p>
      </div>
    );
  }

  return (
    <div className="ps-props">
      <h3>Properties · {props.type}</h3>

      {motionTools && (
        <div className="ps-props-actions">
          <button
            type="button"
            title="Add in / out / emphasis animation to this layer"
            onClick={() => window.dispatchEvent(new CustomEvent(OPEN_ANIMATION_PANEL_EVENT))}
          >
            Animate…
          </button>
        </div>
      )}

      {motionTools && canvas && isVideoObject(activeObj) && (
        <VideoClipProperties canvas={canvas} obj={activeObj} onChange={onChange} />
      )}

      <div className="ps-props-row">
        <div className="field">
          <label htmlFor="prop-x">X</label>
          <input
            id="prop-x"
            type="number"
            value={props.left}
            onChange={(e) => apply({ left: Number(e.target.value) })}
          />
        </div>
        <div className="field">
          <label htmlFor="prop-y">Y</label>
          <input
            id="prop-y"
            type="number"
            value={props.top}
            onChange={(e) => apply({ top: Number(e.target.value) })}
          />
        </div>
      </div>

      <div className="ps-props-row">
        <div className="field">
          <label>W</label>
          <input type="number" value={props.width} readOnly />
        </div>
        <div className="field">
          <label>H</label>
          <input type="number" value={props.height} readOnly />
        </div>
      </div>

      <div className="ps-props-row">
        <div className="field">
          <label htmlFor="prop-angle">Angle</label>
          <input
            id="prop-angle"
            type="number"
            value={props.angle}
            onChange={(e) => apply({ angle: Number(e.target.value) })}
          />
        </div>
        <div className="field">
          <label htmlFor="prop-opacity">Opacity</label>
          <input
            id="prop-opacity"
            type="number"
            min={0}
            max={1}
            step={0.05}
            value={props.opacity}
            onChange={(e) => apply({ opacity: Number(e.target.value) })}
          />
        </div>
      </div>

      <div className="ps-props-row">
        <div className="field">
          <label htmlFor="prop-fill">Fill</label>
          <input
            id="prop-fill"
            type="color"
            value={props.fill}
            onChange={(e) => apply({ fill: e.target.value })}
          />
        </div>
        <div className="field">
          <label htmlFor="prop-stroke">Stroke</label>
          <input
            id="prop-stroke"
            type="color"
            value={props.stroke}
            onChange={(e) =>
              apply({
                stroke: e.target.value,
                strokeWidth: props.strokeWidth > 0 ? props.strokeWidth : 1,
              })
            }
          />
        </div>
      </div>

      <div className="field">
        <label htmlFor="prop-stroke-w">Stroke width</label>
        <input
          id="prop-stroke-w"
          type="number"
          min={0}
          max={64}
          step={0.5}
          value={props.strokeWidth}
          onChange={(e) => apply({ strokeWidth: Number(e.target.value) })}
        />
      </div>

      {props.type === 'rect' && (
        <div className="field">
          <label htmlFor="prop-radius">Corner radius</label>
          <input
            id="prop-radius"
            type="number"
            min={0}
            max={400}
            step={1}
            value={props.cornerRadius}
            onChange={(e) => apply({ cornerRadius: Number(e.target.value) })}
          />
        </div>
      )}

      <div className="field">
        <label htmlFor="prop-blend">Blend mode</label>
        <select
          id="prop-blend"
          value={props.blendMode}
          onChange={(e) =>
            apply({ blendMode: normalizeBlendMode(e.target.value) })
          }
        >
          {BLEND_MODES.map((mode) => (
            <option key={mode.id} value={mode.id}>
              {mode.label}
            </option>
          ))}
        </select>
      </div>

      <h3>Drop shadow</h3>
      <label className="ps-field" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <input
          type="checkbox"
          checked={shadow.enabled}
          onChange={(e) => {
            if (!canvas) return;
            const obj = canvas.getActiveObject();
            if (!obj) return;
            const next = applyObjectShadow(obj, { enabled: e.target.checked });
            setShadow(next);
            canvas.requestRenderAll();
            onChange?.();
          }}
        />
        Enable shadow
      </label>
      {shadow.enabled && (
        <>
          <div className="ps-props-row">
            <div className="field">
              <label htmlFor="sh-color">Color</label>
              <input
                id="sh-color"
                type="color"
                value={shadow.color}
                onChange={(e) => {
                  if (!canvas) return;
                  const obj = canvas.getActiveObject();
                  if (!obj) return;
                  const next = applyObjectShadow(obj, { color: e.target.value });
                  setShadow(next);
                  canvas.requestRenderAll();
                  onChange?.();
                }}
              />
            </div>
            <div className="field">
              <label htmlFor="sh-blur">Blur</label>
              <input
                id="sh-blur"
                type="number"
                min={0}
                max={64}
                value={shadow.blur}
                onChange={(e) => {
                  if (!canvas) return;
                  const obj = canvas.getActiveObject();
                  if (!obj) return;
                  const next = applyObjectShadow(obj, {
                    blur: Number(e.target.value),
                  });
                  setShadow(next);
                  canvas.requestRenderAll();
                  onChange?.();
                }}
              />
            </div>
          </div>
          <div className="ps-props-row">
            <div className="field">
              <label htmlFor="sh-x">Offset X</label>
              <input
                id="sh-x"
                type="number"
                value={shadow.offsetX}
                onChange={(e) => {
                  if (!canvas) return;
                  const obj = canvas.getActiveObject();
                  if (!obj) return;
                  const next = applyObjectShadow(obj, {
                    offsetX: Number(e.target.value),
                  });
                  setShadow(next);
                  canvas.requestRenderAll();
                  onChange?.();
                }}
              />
            </div>
            <div className="field">
              <label htmlFor="sh-y">Offset Y</label>
              <input
                id="sh-y"
                type="number"
                value={shadow.offsetY}
                onChange={(e) => {
                  if (!canvas) return;
                  const obj = canvas.getActiveObject();
                  if (!obj) return;
                  const next = applyObjectShadow(obj, {
                    offsetY: Number(e.target.value),
                  });
                  setShadow(next);
                  canvas.requestRenderAll();
                  onChange?.();
                }}
              />
            </div>
          </div>
        </>
      )}

      <h3>Transform</h3>
      <div className="ps-props-actions">
        <button
          type="button"
          title="Flip horizontal"
          onClick={() => {
            if (!canvas) return;
            if (flipSelection(canvas, 'x')) onChange?.();
          }}
        >
          Flip H
        </button>
        <button
          type="button"
          title="Flip vertical"
          onClick={() => {
            if (!canvas) return;
            if (flipSelection(canvas, 'y')) onChange?.();
          }}
        >
          Flip V
        </button>
        <button
          type="button"
          title="Rotate −90°"
          onClick={() => {
            if (!canvas) return;
            if (rotateSelection(canvas, -90)) onChange?.();
          }}
        >
          −90°
        </button>
        <button
          type="button"
          title="Rotate +90°"
          onClick={() => {
            if (!canvas) return;
            if (rotateSelection(canvas, 90)) onChange?.();
          }}
        >
          +90°
        </button>
      </div>

      <h3>Gradient</h3>
      <div className="ps-props-row">
        <div className="field">
          <label htmlFor="grad-from">From</label>
          <input
            id="grad-from"
            type="color"
            value={selectedColor}
            onChange={(e) => setSelectedColor(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="grad-to">To</label>
          <input
            id="grad-to"
            type="color"
            value={gradientToColor}
            onChange={(e) => setGradientToColor(e.target.value)}
          />
        </div>
      </div>
      <div className="ps-props-actions">
        <button
          type="button"
          onClick={() => {
            if (!canvas) return;
            if (
              applyGradientFill(canvas, 'linear', {
                from: selectedColor,
                to: gradientToColor,
              })
            ) {
              onChange?.();
            }
          }}
        >
          Linear
        </button>
        <button
          type="button"
          onClick={() => {
            if (!canvas) return;
            if (
              applyGradientFill(canvas, 'radial', {
                from: selectedColor,
                to: gradientToColor,
              })
            ) {
              onChange?.();
            }
          }}
        >
          Radial
        </button>
      </div>

      <h3>Mask</h3>
      <div className="ps-props-actions">
        <button
          type="button"
          title="Select image under a shape, then Create mask"
          disabled={selectionCount !== 2}
          onClick={() => {
            if (!canvas) return;
            if (applyClippingMask(canvas)) onChange?.();
          }}
        >
          Create mask
        </button>
        <button
          type="button"
          disabled={!canvas?.getActiveObject()?.clipPath}
          onClick={() => {
            if (!canvas) return;
            if (clearClipPath(canvas)) onChange?.();
          }}
        >
          Clear mask
        </button>
      </div>
      <p className="ps-props-hint">
        Stack image then shape (2 selected) · or select a Frame and Upload an image
      </p>

      {props.type === 'image' && (
        <>
          <h3>Adjustments</h3>
          <div className="field">
            <label htmlFor="flt-brightness">Brightness</label>
            <input
              id="flt-brightness"
              type="range"
              min={-1}
              max={1}
              step={0.05}
              value={filters.brightness}
              onChange={(e) => {
                if (!canvas) return;
                const obj = canvas.getActiveObject();
                if (!obj || obj.type !== 'image') return;
                const next = applyImageFilters(canvas, obj as fabric.Image, {
                  brightness: Number(e.target.value),
                });
                setFilters(next);
                onChange?.();
              }}
            />
          </div>
          <div className="field">
            <label htmlFor="flt-contrast">Contrast</label>
            <input
              id="flt-contrast"
              type="range"
              min={-1}
              max={1}
              step={0.05}
              value={filters.contrast}
              onChange={(e) => {
                if (!canvas) return;
                const obj = canvas.getActiveObject();
                if (!obj || obj.type !== 'image') return;
                const next = applyImageFilters(canvas, obj as fabric.Image, {
                  contrast: Number(e.target.value),
                });
                setFilters(next);
                onChange?.();
              }}
            />
          </div>
          <div className="field">
            <label htmlFor="flt-blur">Blur</label>
            <input
              id="flt-blur"
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={filters.blur}
              onChange={(e) => {
                if (!canvas) return;
                const obj = canvas.getActiveObject();
                if (!obj || obj.type !== 'image') return;
                const next = applyImageFilters(canvas, obj as fabric.Image, {
                  blur: Number(e.target.value),
                });
                setFilters(next);
                onChange?.();
              }}
            />
          </div>
          <div className="field">
            <label htmlFor="flt-saturation">Saturation</label>
            <input
              id="flt-saturation"
              type="range"
              min={-1}
              max={1}
              step={0.05}
              value={filters.saturation}
              onChange={(e) => {
                if (!canvas) return;
                const obj = canvas.getActiveObject();
                if (!obj || obj.type !== 'image') return;
                const next = applyImageFilters(canvas, obj as fabric.Image, {
                  saturation: Number(e.target.value),
                });
                setFilters(next);
                onChange?.();
              }}
            />
          </div>
          <label className="ps-field" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="checkbox"
              checked={filters.grayscale}
              onChange={(e) => {
                if (!canvas) return;
                const obj = canvas.getActiveObject();
                if (!obj || obj.type !== 'image') return;
                const next = applyImageFilters(canvas, obj as fabric.Image, {
                  grayscale: e.target.checked,
                });
                setFilters(next);
                onChange?.();
              }}
            />
            Grayscale
          </label>
          <div className="ps-props-actions">
            <button
              type="button"
              onClick={() => {
                if (!canvas) return;
                const obj = canvas.getActiveObject();
                if (!obj || obj.type !== 'image') return;
                resetImageFilters(canvas, obj as fabric.Image);
                setFilters({
                  brightness: 0,
                  contrast: 0,
                  blur: 0,
                  saturation: 0,
                  grayscale: false,
                });
                onChange?.();
              }}
            >
              Reset filters
            </button>
          </div>

          <h3>AI / Retouch</h3>
          <p className="ps-props-hint">
            Select one image on the artboard — effects apply only to that image.
            {motionTools
              ? ''
              : ' For capture, skin tone, suit, social BG, and reels use Photo Studio.'}
          </p>
          <div className="ps-props-actions">
            {(['natural', 'soft', 'studio'] as BeautyPresetId[]).map((id) => (
              <button
                key={id}
                type="button"
                disabled={bgBusy || cartoonBusy}
                title={`Beauty preset: ${id}`}
                onClick={() => {
                  if (!canvas) return;
                  const obj = canvas.getActiveObject();
                  if (!obj || obj.type !== 'image') {
                    setBgNote('Select a single image first');
                    return;
                  }
                  const next = applyBeautyPreset(canvas, obj as fabric.Image, id);
                  setFilters(next);
                  setBgNote(`Beauty: ${id}`);
                  onChange?.();
                }}
              >
                {id[0].toUpperCase() + id.slice(1)}
              </button>
            ))}
            {motionTools ? null : (
            <button
              type="button"
              title="Open Photo Studio in the libraries panel"
              onClick={() => {
                window.dispatchEvent(new CustomEvent(OPEN_PHOTO_STUDIO_EVENT));
              }}
            >
              Photo Studio
            </button>
            )}
          </div>
          <div className="ps-props-actions">
            <button
              type="button"
              disabled={bgBusy || cartoonBusy}
              title="Remove background from the selected image"
              onClick={() => {
                if (!canvas) return;
                const obj = canvas.getActiveObject();
                if (!obj || obj.type !== 'image') {
                  setBgNote('Select a single image first');
                  return;
                }
                setBgBusy(true);
                setBgNote(null);
                void removeBackgroundFromImage(canvas, obj as fabric.Image)
                  .then((result) => {
                    if (result.mode === 'cloud') {
                      const rem = result.creditsRemaining;
                      setBgNote(
                        rem !== undefined && Number.isFinite(rem)
                          ? `Background removed (cloud) · ${rem} credits left`
                          : 'Background removed (cloud)'
                      );
                    } else {
                      setBgNote('Background removed (local flood-fill)');
                    }
                    onChange?.();
                  })
                  .catch((err) => {
                    setBgNote(
                      err instanceof Error ? err.message : 'Remove background failed'
                    );
                  })
                  .finally(() => setBgBusy(false));
              }}
            >
              {bgBusy ? 'Removing…' : 'Remove BG'}
            </button>
            <button
              type="button"
              disabled={bgBusy || cartoonBusy}
              title="Comic / ink outline look on the selected image"
              onClick={() => {
                if (!canvas) return;
                const obj = canvas.getActiveObject();
                if (!obj || obj.type !== 'image') {
                  setBgNote('Select a single image first');
                  return;
                }
                setCartoonBusy(true);
                setBgNote(null);
                void applyCartoonToImage(canvas, obj as fabric.Image, {
                  style: 'comic',
                })
                  .then(() => {
                    setBgNote('Comic style applied');
                    setFilters({
                      brightness: 0,
                      contrast: 0,
                      blur: 0,
                      saturation: 0,
                      grayscale: false,
                    });
                    onChange?.();
                  })
                  .catch((err) => {
                    setBgNote(
                      err instanceof Error ? err.message : 'Comic effect failed'
                    );
                  })
                  .finally(() => setCartoonBusy(false));
              }}
            >
              {cartoonBusy ? 'Working…' : 'Comic'}
            </button>
            <button
              type="button"
              disabled={bgBusy || cartoonBusy}
              title="Softer cartoon / cel-shade look on the selected image"
              onClick={() => {
                if (!canvas) return;
                const obj = canvas.getActiveObject();
                if (!obj || obj.type !== 'image') {
                  setBgNote('Select a single image first');
                  return;
                }
                setCartoonBusy(true);
                setBgNote(null);
                void applyCartoonToImage(canvas, obj as fabric.Image, {
                  style: 'cartoon',
                })
                  .then(() => {
                    setBgNote('Cartoon style applied');
                    setFilters({
                      brightness: 0,
                      contrast: 0,
                      blur: 0,
                      saturation: 0,
                      grayscale: false,
                    });
                    onChange?.();
                  })
                  .catch((err) => {
                    setBgNote(
                      err instanceof Error ? err.message : 'Cartoon effect failed'
                    );
                  })
                  .finally(() => setCartoonBusy(false));
              }}
            >
              {cartoonBusy ? 'Working…' : 'Cartoon'}
            </button>
          </div>
          {bgNote && <p className="ps-props-hint">{bgNote}</p>}
        </>
      )}

      {props.text !== undefined && (
        <>
          <div className="field">
            <label htmlFor="prop-text">Text</label>
            <input
              id="prop-text"
              value={props.text}
              onChange={(e) => apply({ text: e.target.value })}
            />
          </div>
          <div className="field">
            <label htmlFor="prop-font">Font</label>
            <select
              id="prop-font"
              value={props.fontFamily || FONT_FAMILIES[0].family}
              onChange={(e) => apply({ fontFamily: e.target.value })}
              style={{ fontFamily: props.fontFamily || FONT_FAMILIES[0].family }}
            >
              {FONT_FAMILIES.map((f) => (
                <option key={f.family} value={f.family} style={{ fontFamily: f.family }}>
                  {f.label}
                </option>
              ))}
              {props.fontFamily &&
                !FONT_FAMILIES.some((f) => f.family === props.fontFamily) && (
                  <option value={props.fontFamily}>{props.fontFamily}</option>
                )}
            </select>
          </div>
          <div className="field">
            <label htmlFor="prop-fontsize">Font size</label>
            <input
              id="prop-fontsize"
              type="number"
              min={8}
              max={300}
              value={props.fontSize || 16}
              onChange={(e) => apply({ fontSize: Number(e.target.value) })}
            />
          </div>
          <h3>Style</h3>
          <div className="ps-props-actions ps-text-style">
            <button
              type="button"
              title="Bold"
              className={isBoldWeight(props.fontWeight) ? 'active' : ''}
              aria-pressed={isBoldWeight(props.fontWeight)}
              onClick={() =>
                apply({
                  fontWeight: isBoldWeight(props.fontWeight) ? 'normal' : 'bold',
                })
              }
            >
              <strong>B</strong>
            </button>
            <button
              type="button"
              title="Italic"
              className={props.fontStyle === 'italic' ? 'active' : ''}
              aria-pressed={props.fontStyle === 'italic'}
              onClick={() =>
                apply({
                  fontStyle: props.fontStyle === 'italic' ? 'normal' : 'italic',
                })
              }
            >
              <em>I</em>
            </button>
            <button
              type="button"
              title="Underline"
              className={props.underline ? 'active' : ''}
              aria-pressed={Boolean(props.underline)}
              onClick={() => apply({ underline: !props.underline })}
            >
              <span className="ps-text-style-u">U</span>
            </button>
          </div>
          <div className="ps-props-actions">
            <button
              type="button"
              title="Align left"
              className={props.textAlign === 'left' || !props.textAlign ? 'active' : ''}
              onClick={() => apply({ textAlign: 'left' })}
            >
              Left
            </button>
            <button
              type="button"
              title="Align center"
              className={props.textAlign === 'center' ? 'active' : ''}
              onClick={() => apply({ textAlign: 'center' })}
            >
              Center
            </button>
            <button
              type="button"
              title="Align right"
              className={props.textAlign === 'right' ? 'active' : ''}
              onClick={() => apply({ textAlign: 'right' })}
            >
              Right
            </button>
            <button
              type="button"
              title="Justify"
              className={props.textAlign === 'justify' ? 'active' : ''}
              onClick={() => apply({ textAlign: 'justify' })}
            >
              Justify
            </button>
          </div>
        </>
      )}

      <h3>Align</h3>
      <div className="ps-props-actions">
        <button type="button" title="Align left" onClick={() => align('left')}>
          Left
        </button>
        <button type="button" title="Align center" onClick={() => align('center')}>
          Center
        </button>
        <button type="button" title="Align right" onClick={() => align('right')}>
          Right
        </button>
        <button type="button" title="Align top" onClick={() => align('top')}>
          Top
        </button>
        <button type="button" title="Align middle" onClick={() => align('middle')}>
          Middle
        </button>
        <button type="button" title="Align bottom" onClick={() => align('bottom')}>
          Bottom
        </button>
      </div>
      <p className="ps-props-hint">
        {selectionCount > 1
          ? 'Multi-select: align within selection bounds'
          : 'Single object: align to artboard'}
      </p>

      <h3>Distribute</h3>
      <div className="ps-props-actions">
        <button
          type="button"
          title="Space evenly horizontally"
          disabled={selectionCount < 2}
          onClick={() => distribute('horizontal')}
        >
          Horizontal
        </button>
        <button
          type="button"
          title="Space evenly vertically"
          disabled={selectionCount < 2}
          onClick={() => distribute('vertical')}
        >
          Vertical
        </button>
      </div>
      <p className="ps-props-hint">Select 2+ objects to space gaps evenly</p>

      <h3>Group</h3>
      <div className="ps-props-actions">
        <button
          type="button"
          title="Group (Ctrl+G)"
          disabled={selectionCount < 2}
          onClick={group}
        >
          Group
        </button>
        <button
          type="button"
          title="Ungroup (Ctrl+Shift+G)"
          disabled={activeType !== 'group'}
          onClick={ungroup}
        >
          Ungroup
        </button>
      </div>

      {pathTextReady && (
        <>
          <h3>Type on path</h3>
          <div className="field">
            <label htmlFor="prop-path-text">Text</label>
            <input
              id="prop-path-text"
              value={pathText}
              onChange={(e) => setPathText(e.target.value)}
            />
          </div>
          <div className="ps-props-actions">
            <button
              type="button"
              title="Place characters along the selected edge"
              onClick={() => {
                if (!canvas || !activeObj) return;
                const result = placeTextOnPath(canvas, activeObj, pathText, {
                  fill: selectedColor,
                });
                setPathTextNote(result.note);
                if (result.ok) onChange?.();
              }}
            >
              Place on path
            </button>
          </div>
          <p className="ps-props-hint">
            Works on paths, lines, polygons, and shape edges. Creates a movable text
            group.
          </p>
          {pathTextNote && <p className="ps-props-hint">{pathTextNote}</p>}
        </>
      )}

      <h3>Pathfinder</h3>
      <div className="ps-props-actions">
        <button
          type="button"
          title="Union — merge shapes"
          disabled={selectionCount < 2}
          onClick={() => pathfinder('union')}
        >
          Union
        </button>
        <button
          type="button"
          title="Subtract — cut upper shapes from back-most"
          disabled={selectionCount < 2}
          onClick={() => pathfinder('subtract')}
        >
          Subtract
        </button>
        <button
          type="button"
          title="Intersect — keep overlap only"
          disabled={selectionCount < 2}
          onClick={() => pathfinder('intersect')}
        >
          Intersect
        </button>
        <button
          type="button"
          title="Exclude — keep non-overlapping parts"
          disabled={selectionCount < 2}
          onClick={() => pathfinder('exclude')}
        >
          Exclude
        </button>
      </div>
      <p className="ps-props-hint">
        Select 2+ shapes (not text/images/groups). Back-most layer is the base for
        Subtract / Intersect / Exclude.
      </p>
      {booleanNote && <p className="ps-props-hint">{booleanNote}</p>}

      <h3>Arrange</h3>
      <div className="ps-props-actions">
        <button type="button" onClick={() => bring('front')}>
          Front
        </button>
        <button type="button" onClick={() => bring('forward')}>
          Forward
        </button>
        <button type="button" onClick={() => bring('backward')}>
          Back
        </button>
        <button type="button" onClick={() => bring('back')}>
          Bottom
        </button>
      </div>
    </div>
  );
}
